import { useCanonical } from '../../hooks/useCanonical';
import { Link } from 'react-router-dom';
import { CONFIG } from '../../constants/config';

/**
 * Public privacy policy.
 *
 * Exists as its own route because the Play Store and App Store both require a
 * publicly reachable URL — a modal inside the portal login does not qualify,
 * and a reviewer cannot reach one.
 *
 * Written to describe what the system ACTUALLY does. The earlier modal claimed
 * "account data is removed within 30 days of account closure"; nothing
 * implements that, and cancelling a subscription deletes nothing. A claim like
 * that is harmless in a modal and a liability on a published policy — it is a
 * representation to customers and a POPIA statement. Do not restore it without
 * building the deletion it promises.
 */
const UPDATED = '12 October 2026';

export default function Privacy() {
  useCanonical('/privacy');
  return (
    <div className="min-h-screen bg-white">
      <nav className="flex items-center justify-between px-8 py-5 border-b">
        <Link to="/" className="text-xl font-bold text-gray-900">PatrolIt</Link>
        <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">← Back</Link>
      </nav>

      <main className="max-w-3xl mx-auto px-8 py-12 text-gray-700 leading-relaxed">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-400 mb-10">Last updated {UPDATED}</p>

        <Section title="Who we are">
          <p>
            PatrolIt is patrol-monitoring software operated by <strong>Lenscap Studios</strong>,
            a private company registered in South Africa (registration number K2023650167).
            It is sold to organisations — residential estates, neighbourhood watches
            and businesses — who use it to monitor their own security patrols.
          </p>
          <p className="mt-3">
            Each customer organisation is the responsible party for the personal
            information of its own patrollers. PatrolIt processes that information on
            their behalf.
          </p>
        </Section>

        <Section title="What the patroller app collects">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Location, including in the background.</strong> While a patrol is
              running, the app records the device's GPS position so the control room
              can see the patrol on a live map and replay the route afterwards. It is
              also what the optional safety checks run on: whether the patroller is
              back by the time they set, and whether they have stopped moving.
              Background collection is necessary because a patroller's phone is
              normally in a pocket, not held open.
            </li>
            <li>
              <strong>Safety contacts</strong>, if the patroller adds any - a name and
              an email address for each person they want told if something goes wrong.
              Each contact receives one email when added, explaining why, and can ask to
              be removed.
            </li>
            <li>
              <strong>Incident and alarm reports</strong> — the position where they
              were raised, the time, and any note the patroller types.
            </li>
            <li>
              <strong>The patroller's display name</strong> and a hashed PIN, created
              by their site administrator. Patrollers do not register themselves.
            </li>
          </ul>
          <p className="mt-4">
            Location is collected <strong>only while a patrol is active</strong>. Ending
            the patrol stops collection.
          </p>
        </Section>

        <Section title="What the portal collects">
          <p>
            Name, email address and a hashed password for administrators, control-room
            operators and trustees; the site's name, address and alarm-notification
            email addresses; and billing identifiers returned by our payment provider.
            We never see or store card details.
          </p>
        </Section>

        <Section title="How it is used">
          <p>
            Solely to run the service: showing patrols on the live map, storing patrol
            history and reports, sending alarm notifications to the site's nominated
            addresses, and billing the subscription.
          </p>
          <p className="mt-3">
            <strong>We do not sell personal information, and we do not use it for
            advertising or profiling.</strong> There is no advertising or analytics SDK
            in the patroller app.
          </p>
        </Section>

        <Section title="Who it is shared with">
          <p>
            Within a site, patrol data is visible only to that site's authorised portal
            users. Sites are isolated from one another. We use these processors:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-3">
            <li>DigitalOcean — hosting (Frankfurt, Germany)</li>
            <li>Mailgun — alarm and account emails</li>
            <li>PayFast — subscription payments</li>
            <li>Expo — application updates</li>
          </ul>
          <p className="mt-3">
            <strong>People the patroller chooses</strong> also see some patrol data:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-3">
            <li>
              Anyone the patroller sends a <strong>live tracking link</strong> to can follow
              that patrol live, see any alarm raised on it, and see the finished route for
              48 hours after it ends. The patroller can switch the link off at any time.
            </li>
            <li>
              The patroller's <strong>safety contacts</strong>, and the site's alarm
              contacts, are emailed the patroller's name and last known position if an
              alarm is raised, or if a safety check the patroller set goes unanswered -
              and again when the patrol ends. A patroller can remove a contact at any time.
            </li>
          </ul>
          <p className="mt-3">
            Hosting is in Germany, so information is transferred outside South Africa.
          </p>
        </Section>

        <Section title="How long it is kept">
          <p>
            Patrol history, incidents and alarms are kept for as long as the site's
            account exists, because that record is the point of the product — a site
            may need to produce it for an insurer, a committee or the police.
          </p>
          <p className="mt-3">
            Safety contacts are kept until the patroller removes them, or until the
            patroller's account is deleted. A live tracking link stops working 48 hours
            after its patrol ends.
          </p>
          <p className="mt-3">
            A site can ask us to delete its data at any time, and we will do so within
            30 days. Cancelling a subscription does not by itself delete anything;
            ask us if that is what you want.
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            Under the Protection of Personal Information Act you may ask what personal
            information we hold about you, ask for it to be corrected, or ask for it to
            be deleted.
          </p>
          <p className="mt-3">
            <strong>Patrollers:</strong> your patrol records belong to the site you work
            for. Ask your site administrator first — they control that data. If you
            cannot reach them, contact us and we will pass the request on.
          </p>
        </Section>

        <Section title="Security">
          <p>
            All traffic is encrypted in transit with TLS. Passwords and patroller PINs
            are stored hashed, never in plain text. Databases are backed up daily and
            the backups are encrypted at rest by our hosting provider.
          </p>
        </Section>

        <Section title="Children">
          <p>PatrolIt is a workplace tool and is not intended for anyone under 18.</p>
        </Section>

        <Section title="Contact">
          <p>
            Privacy questions, access requests and deletion requests:{' '}
            <a href={`mailto:${CONFIG.CONTACT_EMAIL}`} className="text-brand-600 hover:underline">
              {CONFIG.CONTACT_EMAIL}
            </a>
          </p>
          <p className="mt-3 text-sm text-gray-500">
            If you are a patroller, please contact your site administrator first —
            they are responsible for your patrol data.
          </p>
        </Section>

        <p className="text-sm text-gray-400 mt-12 pt-8 border-t">
          We will post any changes to this policy on this page and update the date above.
          {' · '}
          <Link to="/terms" className="underline">Terms of Service</Link>
        </p>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}
